import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  Edit3,
  Eye,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  ArrowUpDown,
  Check,
  X,
  GripVertical,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import api from '../lib/api';
import Modal from '../components/ui/Modal';
import FileInput from '../components/ui/FileInput';
import QuestionFormFields from '../components/questions/QuestionFormFields';
import { MOBILE_COLORS } from '../components/questions/ExamPreviewFrame';
import MockTestScoresDrawer from '../components/mock-tests/MockTestScoresDrawer';
import {
  emptyForm as emptyQuestionForm,
  blocksValidationError,
  toPayload as questionToPayload,
} from '../lib/questionForm';
import {
  MOCK_TYPES,
  TIERS,
  type MockTestForm,
  type PickerSection,
  type QuestionSort,
  type QuestionAssignment,
  emptyForm,
  normalizeAssignments,
  reorderAssignments,
  toPayload,
  countMismatch,
  getQuestionLabel,
  createSetId,
} from '../lib/mockTestForm';

function getErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) return err.response?.data?.error?.message || err.message || fallback;
  return fallback;
}

interface MockTestQuestionAssignment {
  _id?: string;
  order?: number;
  setId?: string;
  questionId?: string | { _id: string };
}

interface EditableMockTest {
  _id: string;
  title?: { en?: string; ne?: string };
  description?: { en?: string; ne?: string };
  type?: string;
  tier?: string;
  readingQuestions?: (MockTestQuestionAssignment | QuestionItem)[];
  listeningQuestions?: (MockTestQuestionAssignment | QuestionItem)[];
  questionSets?: { id: string; title: string; section: 'reading' | 'listening' }[];
  timeLimitMinutes?: number;
  totalMarks?: number;
  readingQuestionCount?: number;
  listeningQuestionCount?: number;
  passingMarks?: number;
  thumbnailUrl?: string;
  isActive?: boolean;
}

interface QuestionItem {
  _id: string;
  types?: string[];
  category: string;
  subcategory?: string;
  difficulty: string;
  tier: string;
  publishStatus?: string;
  tags?: string[];
  name?: string;
  ubtPrompt?: {
    stem?: string;
  };
  question?: {
    text?: string;
    imageUrl?: string;
    audioUrl?: string;
    format?: string;
  };
}

function QuestionSelectionSummary({
  label,
  assignments,
  questionMap,
  availableSets,
  targetCount,
  onOpen,
  onAddQuestion,
  onRemove,
  onReorder,
  onCreateSet,
  onAssignmentSetChange,
}: {
  label: string;
  assignments: QuestionAssignment[];
  questionMap: Map<string, QuestionItem>;
  availableSets: Array<{ id: string; title: string; section: 'reading' | 'listening' }>;
  targetCount: number;
  onOpen: () => void;
  onAddQuestion: () => void;
  onRemove: (id: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onCreateSet: (title: string) => void;
  onAssignmentSetChange: (questionId: string, setId?: string) => void;
}) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [newSetTitle, setNewSetTitle] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [openSlotMenu, setOpenSlotMenu] = useState<number | null>(null);

  // Exam-palette layout: one numbered box per target slot. Filled slots
  // (an assignment exists at that position) and empty slots (still need a
  // question) are grouped into contiguous "runs" by setId, so a shared-stem
  // group renders as a single flex unit with a bottom-border connector line
  // and its title above -- the run never splits across a flex-wrap because
  // it's one flex item internally, not N.
  type SlotItem =
    | { kind: 'filled'; assignment: QuestionAssignment; index: number }
    | { kind: 'empty'; slot: number };
  const runs: Array<{ setId: string | null; setTitle: string | null; items: SlotItem[] }> = [];
  const pushItem = (item: SlotItem, setId: string | null, setTitle: string | null) => {
    const last = runs[runs.length - 1];
    if (setId && last?.setId === setId) {
      last.items.push(item);
    } else {
      runs.push({ setId, setTitle, items: [item] });
    }
  };
  assignments.forEach((assignment, index) => {
    const set = assignment.setId ? availableSets.find((s) => s.id === assignment.setId) : undefined;
    pushItem({ kind: 'filled', assignment, index }, set?.id ?? null, set?.title ?? null);
  });
  for (let slot = assignments.length + 1; slot <= targetCount; slot++) {
    pushItem({ kind: 'empty', slot }, null, null);
  }

  const expandedAssignment = expandedId
    ? assignments.find((a) => a.questionId === expandedId)
    : undefined;

  return (
    <div
      className="rounded-2xl border p-4"
      style={{ backgroundColor: MOBILE_COLORS.infoBarBg, borderColor: MOBILE_COLORS.border }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">{label}</p>
          <p className="mt-1 text-xs font-medium">
            <span
              className={assignments.length === targetCount ? 'text-emerald-600' : 'text-amber-600'}
            >
              {assignments.length}
            </span>
            <span className="text-gray-400"> / {targetCount} questions</span>
          </p>
        </div>
        <button type="button" onClick={onOpen} className="btn-secondary text-xs h-fit">
          Browse Questions
        </button>
      </div>

      <div className="mt-3">
        {runs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white px-3 py-2 text-xs text-gray-400">
            Set a target count above to add questions
          </div>
        ) : (
          <div className="flex flex-wrap items-start gap-3">
            {runs.map((run, runIndex) => (
              <div key={run.setId ?? `run-${runIndex}`} className="flex flex-col items-start gap-1">
                {run.setTitle && (
                  <span className="px-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    {run.setTitle}
                  </span>
                )}
                <div
                  className={`flex gap-1 ${run.setId ? 'border-b-2 pb-1' : ''}`}
                  style={run.setId ? { borderColor: MOBILE_COLORS.orange } : undefined}
                >
                  {run.items.map((item) =>
                    item.kind === 'filled' ? (
                      <button
                        key={item.assignment.questionId}
                        type="button"
                        draggable
                        onDragStart={() => setDraggedIndex(item.index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (draggedIndex === null) return;
                          onReorder(draggedIndex, item.index);
                          setDraggedIndex(null);
                        }}
                        onDragEnd={() => setDraggedIndex(null)}
                        onClick={() =>
                          setExpandedId(
                            expandedId === item.assignment.questionId
                              ? null
                              : item.assignment.questionId,
                          )
                        }
                        title={
                          questionMap.has(item.assignment.questionId)
                            ? getQuestionLabel(questionMap.get(item.assignment.questionId)!)
                            : item.assignment.questionId
                        }
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-xs font-bold text-emerald-800 shadow-sm ring-1 transition ${
                          expandedId === item.assignment.questionId
                            ? 'ring-2 ring-primary-500'
                            : 'ring-emerald-200'
                        } ${draggedIndex === item.index ? 'opacity-60' : ''}`}
                      >
                        {item.assignment.order}
                      </button>
                    ) : (
                      <div key={`empty-${item.slot}`} className="relative">
                        <button
                          type="button"
                          onClick={() =>
                            setOpenSlotMenu(openSlotMenu === item.slot ? null : item.slot)
                          }
                          title="Add a question for this slot"
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-dashed text-gray-400 hover:border-primary-400 hover:text-primary-500"
                          style={{ borderColor: MOBILE_COLORS.border }}
                        >
                          <Plus size={14} />
                        </button>
                        {openSlotMenu === item.slot && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenSlotMenu(null)}
                            />
                            <div
                              className="absolute left-0 top-full z-20 mt-1 w-40 rounded-lg border bg-white p-1 shadow-lg"
                              style={{ borderColor: MOBILE_COLORS.border }}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenSlotMenu(null);
                                  onAddQuestion();
                                }}
                                className="block w-full rounded-md px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
                              >
                                + Add new question
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenSlotMenu(null);
                                  onOpen();
                                }}
                                className="block w-full rounded-md px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
                              >
                                Browse existing
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {expandedAssignment && (
          <div className="mt-3 flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs text-gray-700 shadow-sm ring-1 ring-gray-200">
            <span className="cursor-grab text-gray-400" title="Drag its box to reorder">
              <GripVertical size={14} />
            </span>
            <span className="rounded-md bg-primary-50 px-1.5 py-0.5 font-semibold text-primary-700">
              {expandedAssignment.order}
            </span>
            <span className="flex-1 truncate">
              {questionMap.has(expandedAssignment.questionId)
                ? getQuestionLabel(questionMap.get(expandedAssignment.questionId)!)
                : expandedAssignment.questionId}
            </span>
            <select
              value={expandedAssignment.setId ?? ''}
              onChange={(e) =>
                onAssignmentSetChange(expandedAssignment.questionId, e.target.value || undefined)
              }
              className="rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-500"
              title="Move to a different set"
            >
              <option value="">Ungrouped</option>
              {availableSets.map((set) => (
                <option key={set.id} value={set.id}>
                  {set.title}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                onRemove(expandedAssignment.questionId);
                setExpandedId(null);
              }}
              className="text-gray-400 hover:text-danger-500"
            >
              <X size={12} />
            </button>
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        <input
          type="text"
          value={newSetTitle}
          onChange={(e) => setNewSetTitle(e.target.value)}
          placeholder="New set title (e.g. shared passage)"
          className="input-field text-xs"
        />
        <button
          type="button"
          onClick={() => {
            const trimmed = newSetTitle.trim();
            if (!trimmed) return;
            onCreateSet(trimmed);
            setNewSetTitle('');
          }}
          className="btn-secondary text-xs whitespace-nowrap"
        >
          + New set
        </button>
      </div>
    </div>
  );
}

function QuestionPickerDrawer({
  open,
  title,
  testType,
  section,
  selectedAssignments,
  onClose,
  onApply,
}: {
  open: boolean;
  title: string;
  testType: string;
  section: PickerSection;
  selectedAssignments: QuestionAssignment[];
  onClose: () => void;
  onApply: (assignments: QuestionAssignment[], questions: QuestionItem[]) => void;
}) {
  const [draftAssignments, setDraftAssignments] =
    useState<QuestionAssignment[]>(selectedAssignments);
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [tier, setTier] = useState('');
  const [sort, setSort] = useState<QuestionSort>('newest');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraftAssignments(normalizeAssignments(selectedAssignments));
  }, [open, selectedAssignments, section]);

  const category = section === 'readingQuestions' ? 'reading' : 'listening';

  const { data, isLoading } = useQuery({
    queryKey: ['mock-test-picker-questions', testType, section, search, difficulty, tier],
    queryFn: async () => {
      const res = await api.get('/questions', {
        params: {
          page: 1,
          limit: 100,
          type: testType === 'ubt' ? 'ubt' : undefined,
          category,
          search: search || undefined,
          difficulty: difficulty || undefined,
        },
      });
      return res.data.data as QuestionItem[];
    },
    enabled: open,
  });

  const questions = useMemo(() => {
    const base = (data ?? []).filter((question) => !tier || question.tier === tier);
    const difficultyRank: Record<string, number> = { beginner: 1, intermediate: 2, advanced: 3 };
    return [...base].sort((a, b) => {
      if (sort === 'difficulty')
        return (difficultyRank[a.difficulty] ?? 99) - (difficultyRank[b.difficulty] ?? 99);
      if (sort === 'oldest') return a._id.localeCompare(b._id);
      return b._id.localeCompare(a._id);
    });
  }, [data, sort, tier]);

  const toggleQuestion = (id: string) => {
    setDraftAssignments((current) =>
      current.some((item) => item.questionId === id)
        ? normalizeAssignments(current.filter((item) => item.questionId !== id))
        : normalizeAssignments([...current, { questionId: id, order: current.length + 1 }]),
    );
  };

  const removeSelected = (id: string) =>
    setDraftAssignments((current) =>
      normalizeAssignments(current.filter((item) => item.questionId !== id)),
    );

  const applySelection = () => {
    onApply(
      draftAssignments,
      questions.filter((question) =>
        draftAssignments.some((item) => item.questionId === question._id),
      ),
    );
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40" onClick={onClose}>
      <div
        className="absolute right-0 top-0 h-full w-full max-w-5xl overflow-hidden bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-full">
          <div className="flex h-full w-[62%] flex-col border-r border-gray-200">
            <div className="border-b border-gray-200 px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Search, filter, preview, and select multiple {category} questions.
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    {testType === 'ubt'
                      ? `Only ${category} questions that include UBT usage are shown for this mock test.`
                      : `Only ${category} questions are shown for this mock test.`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-3">
                <div className="relative col-span-2">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Search ${category} questions`}
                    className="input-field pl-9"
                  />
                </div>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  className="input-field"
                >
                  <option value="">All difficulties</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  className="input-field"
                >
                  <option value="">All tiers</option>
                  <option value="free">Free</option>
                  <option value="pro_medium">Pro Medium</option>
                </select>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-gray-500">{questions.length} questions available</div>
                <button
                  type="button"
                  onClick={() =>
                    setSort((current) =>
                      current === 'newest'
                        ? 'oldest'
                        : current === 'oldest'
                          ? 'difficulty'
                          : 'newest',
                    )
                  }
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  <ArrowUpDown size={14} />
                  {sort === 'newest'
                    ? 'Newest first'
                    : sort === 'oldest'
                      ? 'Oldest first'
                      : 'Difficulty'}
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {isLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-gray-400">
                  Loading questions...
                </div>
              ) : questions.length === 0 ? (
                <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-400">
                  No questions match the current filters
                </div>
              ) : (
                <div className="space-y-3">
                  {questions.map((question) => {
                    const selected = draftAssignments.some(
                      (item) => item.questionId === question._id,
                    );
                    return (
                      <button
                        key={question._id}
                        type="button"
                        onClick={() => toggleQuestion(question._id)}
                        className={`w-full rounded-2xl border p-4 text-left transition ${selected ? 'border-primary-500 bg-primary-50/60 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="badge bg-blue-50 text-blue-700">
                                {question.category}
                              </span>
                              {question.subcategory ? (
                                <span className="badge bg-emerald-50 text-emerald-700">
                                  {question.subcategory}
                                </span>
                              ) : null}
                              <span className="badge bg-indigo-50 text-indigo-700">
                                {(question.types ?? []).join(', ')}
                              </span>
                              <span className="badge bg-yellow-50 text-yellow-700">
                                {question.difficulty}
                              </span>
                              <span className="badge bg-purple-50 text-purple-700">
                                {question.tier}
                              </span>
                              {question.publishStatus && (
                                <span className="badge bg-gray-100 text-gray-600">
                                  {question.publishStatus}
                                </span>
                              )}
                            </div>
                            <p className="mt-3 line-clamp-2 text-sm font-medium text-gray-900">
                              {getQuestionLabel(question)}
                            </p>
                            <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                              <span className="font-mono">{question._id}</span>
                              {question.tags && question.tags.length > 0 && (
                                <span className="truncate">
                                  {question.tags.slice(0, 3).join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div
                            className={`mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${selected ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 text-transparent'}`}
                          >
                            <Check size={14} />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="flex h-full w-[38%] flex-col bg-gray-50/70">
            <div className="border-b border-gray-200 px-6 py-5">
              <h4 className="text-sm font-semibold text-gray-900">Selected questions</h4>
              <p className="mt-1 text-xs text-gray-500">
                Arrange by selection order. Remove any item before applying.
              </p>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {draftAssignments.length === 0 ? (
                <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white text-sm text-gray-400">
                  No questions selected
                </div>
              ) : (
                <div className="space-y-2">
                  {draftAssignments.map((assignment, index) => {
                    const question = questions.find((item) => item._id === assignment.questionId);
                    return (
                      <div
                        key={assignment.questionId}
                        draggable
                        onDragStart={() => setDraggedIndex(index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (draggedIndex === null) return;
                          setDraftAssignments((current) =>
                            reorderAssignments(current, draggedIndex, index),
                          );
                          setDraggedIndex(null);
                        }}
                        onDragEnd={() => setDraggedIndex(null)}
                        className={`rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm ${draggedIndex === index ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <button
                            type="button"
                            className="mt-0.5 cursor-grab text-gray-400 active:cursor-grabbing"
                            title="Drag to reorder"
                          >
                            <GripVertical size={14} />
                          </button>
                          <div className="mt-0.5 rounded-lg bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-700">
                            {assignment.order}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="line-clamp-2 text-sm font-medium text-gray-900">
                              {question ? getQuestionLabel(question) : assignment.questionId}
                            </p>
                            <p className="mt-1 truncate text-[11px] text-gray-400">
                              {assignment.questionId}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeSelected(assignment.questionId)}
                            className="text-gray-400 hover:text-danger-500"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>{draftAssignments.length} selected</span>
                <button
                  type="button"
                  onClick={() => setDraftAssignments([])}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </button>
              </div>
              <div className="mt-4 flex gap-3">
                <button type="button" onClick={onClose} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="button" onClick={applySelection} className="btn-primary flex-1">
                  Apply selection
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MockTestFormFields({
  form,
  setForm,
  questionMap,
  upsertQuestions,
}: {
  form: MockTestForm;
  setForm: Dispatch<SetStateAction<MockTestForm>>;
  questionMap: Map<string, QuestionItem>;
  upsertQuestions: (questions: QuestionItem[]) => void;
}) {
  const [pickerSection, setPickerSection] = useState<PickerSection | null>(null);
  const [addQuestionSection, setAddQuestionSection] = useState<PickerSection | null>(null);
  const [newQuestionForm, setNewQuestionForm] = useState(emptyQuestionForm());
  const queryClient = useQueryClient();

  const createQuestionMutation = useMutation({
    mutationFn: async (section: PickerSection) => {
      const res = await api.post('/questions', questionToPayload(newQuestionForm));
      return { question: res.data.data as QuestionItem, section };
    },
    onSuccess: ({ question, section }) => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      upsertQuestions([question]);
      setForm((prev) => ({
        ...prev,
        [section]: normalizeAssignments([...prev[section], { questionId: question._id, order: 0 }]),
      }));
      setAddQuestionSection(null);
      setNewQuestionForm(emptyQuestionForm());
      toast.success('Question created and added');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to create question')),
  });

  const openAddQuestion = (section: PickerSection) => {
    setNewQuestionForm(emptyQuestionForm());
    setAddQuestionSection(section);
  };

  const readingSets = form.questionSets.filter((set) => set.section === 'reading');
  const listeningSets = form.questionSets.filter((set) => set.section === 'listening');

  const applySelection = (
    section: PickerSection,
    assignments: QuestionAssignment[],
    questions: QuestionItem[],
  ) => {
    upsertQuestions(questions);
    setForm({ ...form, [section]: normalizeAssignments(assignments) });
  };

  const removeQuestion = (section: PickerSection, id: string) => {
    setForm({
      ...form,
      [section]: form[section]
        .filter((item) => item.questionId !== id)
        .map((item, index) => ({ ...item, order: index + 1 })),
    });
  };

  const reorderSection = (section: PickerSection, fromIndex: number, toIndex: number) => {
    setForm({
      ...form,
      [section]: reorderAssignments(form[section], fromIndex, toIndex),
    });
  };

  const updateAssignmentSet = (section: PickerSection, questionId: string, setId?: string) => {
    setForm({
      ...form,
      [section]: form[section].map((item) =>
        item.questionId === questionId
          ? { ...item, ...(setId ? { setId } : { setId: undefined }) }
          : item,
      ),
    });
  };

  const createSet = (section: 'reading' | 'listening', title: string) => {
    setForm({
      ...form,
      questionSets: [...form.questionSets, { id: createSetId(section), title, section }],
    });
  };

  return (
    <>
      <div className="-mx-6 -mt-4 mb-4 h-1" style={{ backgroundColor: MOBILE_COLORS.orange }} />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,22rem)_1fr] gap-4 items-start">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title (EN) *</label>
              <input
                type="text"
                value={form.title.en}
                onChange={(e) => setForm({ ...form, title: { ...form.title, en: e.target.value } })}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title (NE)</label>
              <input
                type="text"
                value={form.title.ne}
                onChange={(e) => setForm({ ...form, title: { ...form.title, ne: e.target.value } })}
                className="input-field"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (EN)
              </label>
              <textarea
                value={form.description.en}
                onChange={(e) =>
                  setForm({ ...form, description: { ...form.description, en: e.target.value } })
                }
                className="input-field"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (NE)
              </label>
              <textarea
                value={form.description.ne}
                onChange={(e) =>
                  setForm({ ...form, description: { ...form.description, ne: e.target.value } })
                }
                className="input-field"
                rows={2}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="input-field"
              >
                {MOCK_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
              <select
                value={form.tier}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}
                className="input-field"
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total minutes</label>
              <input
                type="number"
                value={form.timeLimitMinutes}
                onChange={(e) => setForm({ ...form, timeLimitMinutes: Number(e.target.value) })}
                className="input-field"
                min={1}
                max={120}
                required
              />
              {form.readingQuestionCount === 20 && form.listeningQuestionCount === 20 && (
                <p className="mt-1 text-xs text-gray-400">≈35 reading + 35 listening</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reading questions
              </label>
              <input
                type="number"
                value={form.readingQuestionCount}
                onChange={(e) => setForm({ ...form, readingQuestionCount: Number(e.target.value) })}
                className="input-field"
                min={0}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Listening questions
              </label>
              <input
                type="number"
                value={form.listeningQuestionCount}
                onChange={(e) =>
                  setForm({ ...form, listeningQuestionCount: Number(e.target.value) })
                }
                className="input-field"
                min={0}
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pass mark</label>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-gray-500">&gt;</span>
              <input
                type="number"
                value={form.passingMarks}
                onChange={(e) => setForm({ ...form, passingMarks: Number(e.target.value) })}
                className="input-field max-w-[8rem]"
                min={1}
                max={form.readingQuestionCount + form.listeningQuestionCount}
                required
              />
              <span className="text-xs text-gray-400">
                out of {form.readingQuestionCount + form.listeningQuestionCount}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <button
              type="button"
              role="switch"
              aria-checked={form.isActive}
              onClick={() => setForm({ ...form, isActive: !form.isActive })}
              className={`flex items-center gap-3 w-full rounded-lg border px-3 py-2 transition-colors ${
                form.isActive ? 'border-success-500/30 bg-success-50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <span
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  form.isActive ? 'bg-success-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    form.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </span>
              <span className="text-sm text-left">
                <span
                  className={`font-medium ${form.isActive ? 'text-success-700' : 'text-gray-600'}`}
                >
                  {form.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className="block text-xs text-gray-400">
                  {form.isActive
                    ? 'Visible to students in the app'
                    : 'Hidden from the app until reactivated'}
                </span>
              </span>
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <QuestionSelectionSummary
            label="Reading Questions"
            assignments={form.readingQuestions}
            questionMap={questionMap}
            availableSets={readingSets}
            targetCount={form.readingQuestionCount}
            onOpen={() => setPickerSection('readingQuestions')}
            onAddQuestion={() => openAddQuestion('readingQuestions')}
            onRemove={(id) => removeQuestion('readingQuestions', id)}
            onReorder={(fromIndex, toIndex) =>
              reorderSection('readingQuestions', fromIndex, toIndex)
            }
            onCreateSet={(title) => createSet('reading', title)}
            onAssignmentSetChange={(questionId, setId) =>
              updateAssignmentSet('readingQuestions', questionId, setId)
            }
          />

          <QuestionSelectionSummary
            label="Listening Questions"
            assignments={form.listeningQuestions}
            questionMap={questionMap}
            availableSets={listeningSets}
            targetCount={form.listeningQuestionCount}
            onOpen={() => setPickerSection('listeningQuestions')}
            onAddQuestion={() => openAddQuestion('listeningQuestions')}
            onRemove={(id) => removeQuestion('listeningQuestions', id)}
            onReorder={(fromIndex, toIndex) =>
              reorderSection('listeningQuestions', fromIndex, toIndex)
            }
            onCreateSet={(title) => createSet('listening', title)}
            onAssignmentSetChange={(questionId, setId) =>
              updateAssignmentSet('listeningQuestions', questionId, setId)
            }
          />

          {addQuestionSection && (
            <div className="rounded-2xl border-2 border-primary-200 bg-primary-50/40 p-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">
                  New {addQuestionSection === 'readingQuestions' ? 'reading' : 'listening'} question
                </p>
                <button
                  type="button"
                  onClick={() => setAddQuestionSection(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              </div>
              <QuestionFormFields
                form={newQuestionForm}
                setForm={setNewQuestionForm}
                lockedCategory={addQuestionSection === 'readingQuestions' ? 'reading' : 'listening'}
                lockedTypes={
                  form.type === 'ubt' ? ['ubt'] : form.type === 'cbt' ? ['cbt'] : ['quiz']
                }
              />
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAddQuestionSection(null)}
                  className="btn-secondary text-xs"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={
                    createQuestionMutation.isPending ||
                    blocksValidationError(newQuestionForm) !== null
                  }
                  onClick={() => createQuestionMutation.mutate(addQuestionSection!)}
                  className="btn-primary text-xs flex items-center gap-1"
                >
                  {createQuestionMutation.isPending ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Plus size={12} />
                  )}
                  Create & add
                </button>
              </div>
            </div>
          )}

          <FileInput
            label="Thumbnail"
            value={form.thumbnailUrl}
            onChange={(url) => setForm({ ...form, thumbnailUrl: url })}
            accept="image"
            placeholder="Select thumbnail image"
          />
        </div>
      </div>

      <QuestionPickerDrawer
        open={pickerSection !== null}
        title={
          pickerSection === 'readingQuestions'
            ? 'Select Reading Questions'
            : 'Select Listening Questions'
        }
        testType={form.type}
        section={pickerSection ?? 'readingQuestions'}
        selectedAssignments={pickerSection ? form[pickerSection] : []}
        onClose={() => setPickerSection(null)}
        onApply={(assignments, questions) =>
          pickerSection && applySelection(pickerSection, assignments, questions)
        }
      />
    </>
  );
}

function CreateMockTestModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<MockTestForm>(emptyForm());
  const [questionMap, setQuestionMap] = useState<Map<string, QuestionItem>>(new Map());

  const upsertQuestions = (questions: QuestionItem[]) => {
    setQuestionMap((current) => {
      const next = new Map(current);
      questions.forEach((question) => next.set(question._id, question));
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: async (data: MockTestForm) => {
      await api.post('/mock-tests', toPayload(data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mock-tests'] });
      toast.success('Mock test created');
      setForm(emptyForm());
      setQuestionMap(new Map());
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to create mock test')),
  });

  return (
    <Modal open={open} onClose={onClose} title="Create Mock Test" variant="drawer">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const mismatch = countMismatch(form);
          if (mismatch) {
            toast.error(mismatch);
            return;
          }
          mutation.mutate(form);
        }}
        className="space-y-4"
      >
        <MockTestFormFields
          form={form}
          setForm={setForm}
          questionMap={questionMap}
          upsertQuestions={upsertQuestions}
        />
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary flex items-center gap-2"
          >
            {mutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Plus size={16} />
            )}
            Create
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditMockTestModal({
  open,
  onClose,
  test,
}: {
  open: boolean;
  onClose: () => void;
  test: EditableMockTest;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<MockTestForm>(emptyForm());
  const [questionMap, setQuestionMap] = useState<Map<string, QuestionItem>>(() => {
    const initial = new Map<string, QuestionItem>();
    [...(test.readingQuestions || []), ...(test.listeningQuestions || [])].forEach((question) => {
      const resolvedQuestion = (question as MockTestQuestionAssignment).questionId ?? question;
      if (resolvedQuestion && typeof resolvedQuestion !== 'string' && resolvedQuestion._id)
        initial.set(resolvedQuestion._id, resolvedQuestion as QuestionItem);
    });
    return initial;
  });

  useEffect(() => {
    setForm({
      title: { en: test.title?.en || '', ne: test.title?.ne || '' },
      description: { en: test.description?.en || '', ne: test.description?.ne || '' },
      type: test.type || 'ubt',
      tier: test.tier || 'free',
      readingQuestions: (test.readingQuestions || []).map(
        (assignment: MockTestQuestionAssignment, index: number) => ({
          questionId:
            (typeof assignment.questionId === 'object'
              ? assignment.questionId?._id
              : assignment.questionId) ||
            assignment._id ||
            '',
          order: assignment.order || index + 1,
          ...(assignment.setId ? { setId: assignment.setId } : {}),
        }),
      ),
      listeningQuestions: (test.listeningQuestions || []).map(
        (assignment: MockTestQuestionAssignment, index: number) => ({
          questionId:
            (typeof assignment.questionId === 'object'
              ? assignment.questionId?._id
              : assignment.questionId) ||
            assignment._id ||
            '',
          order: assignment.order || index + 1,
          ...(assignment.setId ? { setId: assignment.setId } : {}),
        }),
      ),
      questionSets: test.questionSets || [],
      timeLimitMinutes: test.timeLimitMinutes || 50,
      readingQuestionCount: test.readingQuestionCount ?? (test.readingQuestions || []).length,
      listeningQuestionCount: test.listeningQuestionCount ?? (test.listeningQuestions || []).length,
      passingMarks: test.passingMarks || 24,
      thumbnailUrl: test.thumbnailUrl || '',
      isActive: test.isActive ?? true,
    });
  }, [test]);

  const upsertQuestions = (questions: QuestionItem[]) => {
    setQuestionMap((current) => {
      const next = new Map(current);
      questions.forEach((question) => next.set(question._id, question));
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: async (data: MockTestForm) => {
      await api.put(`/mock-tests/${test._id}`, toPayload(data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mock-tests'] });
      toast.success('Mock test updated');
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update mock test')),
  });

  return (
    <Modal open={open} onClose={onClose} title="Edit Mock Test" variant="drawer">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const mismatch = countMismatch(form);
          if (mismatch) {
            toast.error(mismatch);
            return;
          }
          mutation.mutate(form);
        }}
        className="space-y-4"
      >
        <MockTestFormFields
          form={form}
          setForm={setForm}
          questionMap={questionMap}
          upsertQuestions={upsertQuestions}
        />
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary flex items-center gap-2"
          >
            {mutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Edit3 size={16} />
            )}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function MockTestsPage() {
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [editTest, setEditTest] = useState<EditableMockTest | null>(null);
  const [scoresTest, setScoresTest] = useState<EditableMockTest | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['mock-tests', page],
    queryFn: async () => {
      const res = await api.get('/mock-tests', { params: { page, limit: 20 } });
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/mock-tests/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mock-tests'] });
      toast.success('Mock test deleted');
    },
    onError: () => toast.error('Failed to delete'),
  });

  const tests = data?.data ?? [];
  const pagination = data?.pagination ?? { total: 0, page: 1, totalPages: 1 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mock Tests</h1>
          <p className="text-gray-500 mt-1">{pagination.total} total mock tests</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Create Mock Test
        </button>
      </div>

      <CreateMockTestModal open={showCreate} onClose={() => setShowCreate(false)} />
      {editTest && (
        <EditMockTestModal open={!!editTest} onClose={() => setEditTest(null)} test={editTest} />
      )}
      {scoresTest && (
        <MockTestScoresDrawer
          open={!!scoresTest}
          onClose={() => setScoresTest(null)}
          testId={scoresTest._id}
          testTitle={scoresTest.title?.en || ''}
        />
      )}

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-500">Title</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Type</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Questions</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Duration</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Tier</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : tests.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    No mock tests found
                  </td>
                </tr>
              ) : (
                tests.map((test: EditableMockTest) => (
                  <tr key={test._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium text-gray-900">{test.title?.en || ''}</td>
                    <td className="py-3 px-4">
                      <span className="badge bg-blue-50 text-blue-700">{test.type}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {(test.readingQuestions?.length ?? 0) +
                        (test.listeningQuestions?.length ?? 0)}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{test.timeLimitMinutes}m</td>
                    <td className="py-3 px-4">
                      <span className="badge bg-purple-50 text-purple-700">{test.tier}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`badge ${test.isActive ? 'bg-success-50 text-success-700' : 'bg-gray-100 text-gray-600'}`}
                      >
                        {test.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setScoresTest(test)}
                          className="p-1 text-gray-400 hover:text-primary-600"
                          title="View scores"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={() => setEditTest(test)}
                          className="p-1 text-gray-400 hover:text-primary-600"
                          title="Edit"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => deleteMutation.mutate(test._id)}
                          className="p-1 text-gray-400 hover:text-danger-500"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pagination.totalPages}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
